import React, { useState, useEffect } from "react";
import styles from "./TemplateUploadForm.module.css";

const TemplateUploadForm = ({
  initialData,
  onCancel,
  onSave,
  categories,
  errMessage,
  showUploadForm,
  isLoading,
}) => {
  const [templateName, setTemplateName] = useState("");
  const [dataCategory, setDataCategory] = useState("");
  const [dataCategoryName, setDataCategoryName] = useState("");
  const [status, setStatus] = useState("Active");
  const [fuelType, setFuelType] = useState("");
  const [emissionFactorUsage, setEmissionFactorUsage] = useState("");

  const [openDropdown, setOpenDropdown] = useState(null); // central dropdown control

  const sortedCategories = () => {
    if (!categories) return [];
    return [...categories].sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    if (initialData) {
      setTemplateName(initialData?.name || "");
      setDataCategory(initialData?.category_id || "");
      setDataCategoryName(initialData?.categoryName || "");
      setStatus(initialData?.status === 1 ? "Active" : "Inactive");
      setFuelType(initialData?.fuel_type || "");
      setEmissionFactorUsage(initialData?.emission_factor_usage || "");
    }
  }, [initialData]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${styles.dropdown}`)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSave = () => {
    if (templateName && dataCategory) {
      const payload = {
        title: templateName,
        category_id: dataCategory,
        status,
      };

      if (dataCategoryName?.toLowerCase().includes("fuel")) {
        payload.fuel_type = fuelType;
      }

      if (dataCategoryName?.toLowerCase().includes("electricity")) {
        payload.emission_factor_usage = emissionFactorUsage;
      }

      onSave(payload);
    }
  };

  return (
    <div className={styles.formWrap} style={{ display: !showUploadForm ? "none" : "block" }}>
      <div className={styles.formContainer}>

        {/* Template Name */}
        <div className={styles.formRow}>
          <label>Template Name</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className={styles.inputField}
          />
          <p className={styles.errorText}>{errMessage}</p>
        </div>

        {/* Dropdowns */}
        <div className={styles.dropdownGrid}>

          {/* Data Category */}
          <div className={styles.dropdown}>
            <label className={styles.label}>Data Category</label>
            <div
              className={styles.dropdownHeader}
              onClick={() => setOpenDropdown(openDropdown === 'category' ? null : 'category')}
            >
              {dataCategoryName || "Select Data Category"}
            </div>
            <ul className={`${styles.dropdownList} ${openDropdown !== 'category' && styles.hidden}`}>
              {sortedCategories()?.map((category) => (
                <li
                  key={category.category_id}
                  className={styles.dropdownItem}
                  onClick={() => {
                    setDataCategory(category.category_id);
                    setDataCategoryName(category.name);
                    setOpenDropdown(null);
                  }}
                >
                  {category.name}
                </li>
              ))}
            </ul>
          </div>

          {/* Fuel Type */}
          {dataCategoryName?.toLowerCase().includes("fuel") && (
            <div className={styles.dropdown}>
              <label className={styles.label}>Fuel Type</label>
              <div
                className={styles.dropdownHeader}
                onClick={() => setOpenDropdown(openDropdown === 'fuelType' ? null : 'fuelType')}
              >
                {fuelType || "Select Fuel Type"}
              </div>
              <ul className={`${styles.dropdownList} ${openDropdown !== 'fuelType' && styles.hidden}`}>
                {["Acetylene", "Jet A1", "Coal", "Diesel", "LPG", "Petrol"].map((fuel) => (
                  <li
                    key={fuel}
                    className={styles.dropdownItem}
                    onClick={() => {
                      setFuelType(fuel);
                      setOpenDropdown(null);
                    }}
                  >
                    {fuel}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Emission Factor */}
          {dataCategoryName?.toLowerCase().includes("electricity") && (
            <div className={styles.dropdown}>
              <label className={styles.label}>Emission Factor</label>
              <div
                className={styles.dropdownHeader}
                onClick={() => setOpenDropdown(openDropdown === 'emissionFactor' ? null : 'emissionFactor')}
              >
                {emissionFactorUsage === "default" ? "Electricity Grid" : "Select Option"}
              </div>
              <ul className={`${styles.dropdownList} ${openDropdown !== 'emissionFactor' && styles.hidden}`}>
                <li
                  className={styles.dropdownItem}
                  onClick={() => {
                    setEmissionFactorUsage("default");
                    setOpenDropdown(null);
                  }}
                >
                  Electricity Grid
                </li>
              </ul>
            </div>
          )}

          {/* Status */}
          <div className={styles.dropdown}>
            <label className={styles.label}>Status</label>
            <div
              className={styles.dropdownHeader}
              onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
              style={{ color: status === "Active" ? "#80BA78" : "#D6746F" }}
            >
              {status}
            </div>
            <ul className={`${styles.dropdownList} ${openDropdown !== 'status' && styles.hidden}`}>
              <li
                className={styles.dropdownItem}
                onClick={() => {
                  setStatus("Active");
                  setOpenDropdown(null);
                }}
              >
                Active
              </li>
              <li
                className={styles.dropdownItem}
                onClick={() => {
                  setStatus("Inactive");
                  setOpenDropdown(null);
                }}
              >
                Inactive
              </li>
            </ul>
          </div>

        </div>

        {/* Action Buttons */}
        <div className={styles.buttonGroup}>
          <button onClick={() => { setOpenDropdown(null); onCancel(); }} className={styles.cancelButton}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!templateName || !dataCategory}
            className={styles.saveButton}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M8.58984 16.58L13.1698 12L8.58984 7.41L9.99984 6L15.9998 12L9.99984 18L8.58984 16.58Z"
                fill="#F7F7F8"
              />
            </svg>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateUploadForm;
